import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Group,
  CheckCircleOutline,
  CancelOutlined,
  EventBusy,
  Visibility as VisibilityIcon,
  AssessmentOutlined as ReportIcon,
  AccessTime as AccessTimeIcon,
  EditOutlined,
  CalendarMonth as CalendarMonthIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import "../../styles/attendance.css";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orange;
const BG = "#F8FAFC";

const STATUS_CLASS = {
  P: "present",
  A: "absent",
  O: "offday",
  R: "restday",
  L: "leave",
  H: "holiday",
  E: "early",
};

const StatCard = ({ icon, label, value, iconBg, iconColor, onClick, isActive }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: "pointer",
      bgcolor: "#fff",
      borderRadius: "12px",
      p: 1.25,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1.5,
      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
      border: isActive ? `1px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: NAVY,
          lineHeight: 1.1,
          mb: 0.25,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.7rem",
          color: BRAND.muted,
          fontWeight: 500,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

const formatClockTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const AttendanceCard = ({
  employee,
  onView,
  onMark,
  canGenerateReport,
  isSelected,
  onToggleSelect,
}) => {
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  const statusCode = employee.status || "-";
  const statusClass = STATUS_CLASS[statusCode] || "unknown";
  const clockIn = formatClockTime(employee.clock_in);
  const clockOut = formatClockTime(employee.clock_out);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        mb: 1.25,
        border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.25, py: 0.875 }}>
        {/* Row 1: select · name · status · view */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: canGenerateReport
              ? "24px 1fr auto 72px"
              : "1fr auto 72px",
            alignItems: "center",
            columnGap: 0.75,
            minHeight: 28,
          }}
        >
          {canGenerateReport && (
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => onToggleSelect(employee)}
              sx={{
                p: 0,
                m: 0,
                justifySelf: "center",
                color: BRAND.muted,
                "&.Mui-checked": { color: ORANGE },
              }}
              inputProps={{ "aria-label": `Select ${fullName}` }}
            />
          )}

          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.25,
              minWidth: 0,
            }}
          >
            {fullName || "Unknown"}
          </Typography>

          <span
            className={`attendance-status-badge attendance-status-badge-compact ${statusClass}`}
          >
            {statusCode}
          </span>

          <Box sx={{ display: "flex", gap: 0.5, justifySelf: "center" }}>
            <IconButton
              size="small"
              onClick={() => onMark(employee)}
              aria-label={`Mark ${fullName}`}
              sx={{
                p: 0,
                color: "#ff9800",
                bgcolor: "rgba(255, 152, 0, 0.08)",
                width: 32,
                height: 32,
              }}
            >
              <EditOutlined sx={{ fontSize: "1.05rem" }} />
            </IconButton>

            <IconButton
              size="small"
              onClick={() => onView(employee)}
              aria-label={`View ${fullName}`}
              sx={{
                p: 0,
                color: "#2196f3",
                bgcolor: "rgba(33, 150, 243, 0.08)",
                width: 32,
                height: 32,
              }}
            >
              <VisibilityIcon sx={{ fontSize: "1.05rem" }} />
            </IconButton>
          </Box>
        </Box>

        {/* Row 2: emp id · clock times — aligned under name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.35,
            pl: canGenerateReport ? "calc(24px + 6px)" : 0,
            minWidth: 0,
            color: BRAND.muted,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              color: ORANGE,
              flexShrink: 0,
              lineHeight: 1.2,
            }}
          >
            {employee.employee_id || "N/A"}
          </Typography>
          <Typography component="span" sx={{ fontSize: "0.6875rem", color: BRAND.border }}>
            ·
          </Typography>
          <AccessTimeIcon sx={{ fontSize: "0.75rem", flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: "0.6875rem",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            In {clockIn} · Out {clockOut}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const AttendanceMobileView = ({
  employees,
  searchTerm,
  setSearchTerm,
  filterDate,
  setFilterDate,
  handleView,
  handleMark,
  canGenerateReport,
  selectedRows,
  onToggleSelect,
  onOpenGenerateReport,
  reportButtonRef,
}) => {
  const [statusFilter, setStatusFilter] = useState("all");

  const stats = useMemo(
    () => ({
      total: employees.length,
      present: employees.filter((e) => e.status === "P").length,
      absent: employees.filter((e) => e.status === "A").length,
      onLeave: employees.filter((e) => e.status === "L").length,
    }),
    [employees]
  );

  const filteredList = useMemo(() => {
    if (statusFilter === "all") return employees;
    return employees.filter((e) => e.status === statusFilter);
  }, [employees, statusFilter]);

  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => row.id)),
    [selectedRows]
  );

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY }}>
          Time & Attendance
        </Typography>
        
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {canGenerateReport && (
            <Tooltip
              title={
                selectedRows.length === 0
                  ? "Select employees to generate report"
                  : `Generate report (${selectedRows.length} selected)`
              }
            >
              <span>
                <IconButton
                  ref={reportButtonRef}
                  size="small"
                  disabled={selectedRows.length === 0}
                  onClick={onOpenGenerateReport}
                  aria-label="Generate attendance report"
                  sx={{
                    bgcolor: selectedRows.length > 0 ? ORANGE : "#fff",
                    color: selectedRows.length > 0 ? "#fff" : BRAND.muted,
                    border: `1px solid ${selectedRows.length > 0 ? ORANGE : BRAND.border}`,
                    borderRadius: "10px",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      bgcolor: selectedRows.length > 0 ? BRAND.orangeHover : "#f1f5f9",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#fff",
                      border: `1px solid ${BRAND.border}`,
                    },
                  }}
                >
                  <ReportIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {canGenerateReport && selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            bgcolor: BRAND.orangeLight,
            borderRadius: "10px",
            border: `1px solid ${ORANGE}33`,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY }}>
            {selectedRows.length} employee{selectedRows.length > 1 ? "s" : ""} selected
          </Typography>
        </Box>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <StatCard
            icon={<Group fontSize="small" />}
            label="Total Employees"
            value={stats.total}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
            onClick={() => setStatusFilter("all")}
            isActive={statusFilter === "all"}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<CheckCircleOutline fontSize="small" />}
            label="Present Today"
            value={stats.present}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
            onClick={() => setStatusFilter("P")}
            isActive={statusFilter === "P"}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<CancelOutlined fontSize="small" />}
            label="Absent Today"
            value={stats.absent}
            iconBg="rgba(255,152,0,0.15)"
            iconColor="#ff9800"
            onClick={() => setStatusFilter("A")}
            isActive={statusFilter === "A"}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<EventBusy fontSize="small" />}
            label="On Leave"
            value={stats.onLeave}
            iconBg="rgba(211,47,47,0.12)"
            iconColor="#d32f2f"
            onClick={() => setStatusFilter("L")}
            isActive={statusFilter === "L"}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
        <TextField
          fullWidth
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: "12px",
              "& fieldset": { borderColor: BRAND.border },
              "&.Mui-focused fieldset": { borderColor: ORANGE },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: BRAND.muted }} />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            position: "relative",
            width: 48,
            height: 48,
            flexShrink: 0,
            bgcolor: "white",
            borderRadius: "12px",
            border: `1px solid ${BRAND.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CalendarMonthIcon sx={{ color: BRAND.muted }} />
          <TextField
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            inputProps={{ max: new Date().toISOString().split("T")[0] }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              opacity: 0,
              "& input": { cursor: "pointer", height: "100%", padding: 0 },
            }}
          />
        </Box>
      </Box>

      {filteredList.length > 0 ? (
        filteredList.map((emp) => (
          <AttendanceCard
            key={emp.id}
            employee={emp}
            onView={handleView}
            onMark={handleMark}
            canGenerateReport={canGenerateReport}
            isSelected={selectedIds.has(emp.id)}
            onToggleSelect={onToggleSelect}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <Group sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No employees found
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            Adjust your search or status filter.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AttendanceMobileView;
